'use client';
import { supportsAbortController, supportsRequestStreams, supportsFormData, supportsResponseStreams } from '@s7/platform';
import { isString, isUndefined, invariantSync, invariant } from '@s7/utils';
import { isResponse, isRequest } from '../utils/guards.js';
import { getInternalOptions } from './utils/getInternalOptions.js';
import { getComposedInput } from './utils/getComposedInput.js';
import { getInputCredentials } from './utils/getInputCredentials.js';
import { ResponseStreamsSupportError } from '../domain/errors/ResponseStreamsSupportError.js';
import { watchDownloadStream } from './utils/watchDownloadStream.js';
import { HTTPError } from '../domain/errors/HTTPError.js';
import { maxSafeTimeout } from '../domain/constants.js';
import { TimeoutRangeError } from '../domain/errors/TimeoutRangeError.js';
import { getUnknownOptions } from './utils/getUnknownOptions.js';
import { TimeoutError } from '../domain/errors/TimeoutError.js';
import { JSONParseError } from '../domain/errors/JSONParseError.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
function _create_for_of_iterator_helper_loose(o, allowArrayLike) {
    var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
    if (it) return (it = it.call(o)).next.bind(it);
    if (Array.isArray(o) || (it = _unsupported_iterable_to_array(o)) || allowArrayLike && o && typeof o.length === "number") {
        if (it) o = it;
        var i = 0;
        return function() {
            if (i >= o.length) {
                return {
                    done: true
                };
            }
            return {
                done: false,
                value: o[i++]
            };
        };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
/**
 * Функция собирает параметры fetch на основе `input` и `options`,
 * с которыми далее будет выполнен запрос, а также создаёт набор методов,
 * с определёнными внутри них инвариантами.
 *
 * Отвечает за применение хуков.
 * */ var Client = function(input, options) {
    var fetch = function fetch() {
        return _fetch.apply(this, arguments);
    };
    var applyBeforeRequestHooks = function applyBeforeRequestHooks() {
        return _applyBeforeRequestHooks.apply(this, arguments);
    };
    var applyAfterResponseHooks = function applyAfterResponseHooks(response) {
        return _applyAfterResponseHooks.apply(this, arguments);
    };
    var applyBeforeErrorHooks = function applyBeforeErrorHooks(httpError) {
        return _applyBeforeErrorHooks.apply(this, arguments);
    };
    var execute = function execute() {
        return _execute.apply(this, arguments);
    };
    var internalOptions = getInternalOptions(input, options);
    var composedInput = getComposedInput(input, options.prefixUrl);
    var credentials = getInputCredentials(input);
    //// Далее важен порядок операций инициализации.
    /* Если в `input` присутствуют credentials, добавляем их к параметрам запроса */ if (credentials) {
        internalOptions.credentials = credentials;
    }
    var abortController;
    /* Если есть поддержка AbortController, создаём его и заменяем signal в параметрах запроса. */ if (supportsAbortController) {
        abortController = new globalThis.AbortController();
        if (internalOptions.signal) {
            var originalSignal = internalOptions.signal;
            internalOptions.signal.addEventListener("abort", function() {
                abortController.abort(originalSignal.reason);
            });
        }
        internalOptions.signal = abortController.signal;
    }
    /*
     * Если есть поддержка Streaming API устанавливаем режим
     * попеременной передачи и приёма для дальнейшей работы с ReadableStream.
     * */ if (supportsRequestStreams) {
        // @ts-expect-error - Просроченные типы.
        internalOptions.duplex = "half";
    }
    var initialRequest = new globalThis.Request(composedInput, internalOptions);
    if (internalOptions.searchParams) {
        var textSearchParams = isString(internalOptions.searchParams) ? internalOptions.searchParams.replace(/^\?/, "") : // тем не менее создание URLSearchParams выполняется корректно за счёт внутренних приведений типов.
        new URLSearchParams(internalOptions.searchParams).toString();
        var url = initialRequest.url.replace(/(?:\?.*?)?(?=#|$)/, "?" + textSearchParams);
        var bodyIsFormData = _instanceof(internalOptions.body, globalThis.FormData);
        var bodyIsURLSearchParams = _instanceof(internalOptions.body, URLSearchParams);
        /*
         * Для обеспечения правильной границы формы,
         * заголовок Content-Type должен удаляться каждый раз,
         * когда новый запрос создается из другого.
         */ if ((supportsFormData && bodyIsFormData || bodyIsURLSearchParams) && internalOptions.headers && !internalOptions.headers.has("content-type")) {
            initialRequest.headers.delete("content-type");
        }
        /*
         * Копирование `self.request` обязательно, так как в противном случае
         * свойство `duplex` оказывается не определено и выбрасывается ошибка.
         * */ initialRequest = new globalThis.Request(new globalThis.Request(url, _extends({}, initialRequest)), internalOptions);
    }
    /*
     * Если собираемся передать содержимое, добавляем требующиеся заголовки и `body`
     * в параметры запроса.
     */ if (!isUndefined(internalOptions.json)) {
        internalOptions.body = JSON.stringify(internalOptions.json);
        var _internalOptions_headers_get;
        initialRequest.headers.set("content-type", (_internalOptions_headers_get = internalOptions.headers.get("content-type")) != null ? _internalOptions_headers_get : "application/json");
        initialRequest = new globalThis.Request(initialRequest, {
            body: internalOptions.body
        });
    }
    var self = {
        request: initialRequest,
        options: internalOptions,
        execute: execute
    };
    function _fetch() {
        _fetch = ////
        /**
     * Выполняет запрос, используя Fetch API.
     * Перед запросом выполняет beforeRequest-middleware.
     *
     * По timeout выбрасывает TimeoutError.
     * */ _async_to_generator(function() {
            var result, requestClone, nonRequestOptions;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            applyBeforeRequestHooks()
                        ];
                    case 1:
                        result = _state.sent();
                        if (isResponse(result)) {
                            return [
                                2,
                                result
                            ];
                        }
                        requestClone = self.request.clone();
                        nonRequestOptions = getUnknownOptions(self.request, internalOptions);
                        /* Выполнение без timeout */ if (self.options.timeout === false) {
                            return [
                                2,
                                self.options.fetch(requestClone, nonRequestOptions)
                            ];
                        }
                        /* Выполнение c timeout */ return [
                            2,
                            new Promise(function(resolve, reject) {
                                var timeoutId = setTimeout(function() {
                                    if (abortController) {
                                        abortController.abort();
                                    }
                                    reject(new TimeoutError(requestClone));
                                }, self.options.timeout);
                                void self.options.fetch(requestClone, nonRequestOptions).then(resolve).catch(reject).then(function() {
                                    clearTimeout(timeoutId);
                                });
                            })
                        ];
                }
            });
        });
        return _fetch.apply(this, arguments);
    }
    /**
     * При клонировании модифицирует `response.clone`, чтобы копии также декорировались;
     * Обрабатывает случай с ответом 204 (пустой body).
     * */ function decorateResponse(response) {
        response.json = /*#__PURE__*/ _async_to_generator(function() {
            var text;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            response.text()
                        ];
                    case 1:
                        text = _state.sent();
                        if (response.status === 204) {
                            try {
                                return [
                                    2,
                                    self.options.parseJson(text)
                                ];
                            } catch (e) {
                                return [
                                    2,
                                    null
                                ];
                            }
                        }
                        try {
                            return [
                                2,
                                self.options.parseJson(text)
                            ];
                        } catch (e) {
                            throw new JSONParseError(text, response, self.request, self.options);
                        }
                        return [
                            2
                        ];
                }
            });
        });
        var makeClone = response.clone.bind(response);
        response.clone = function() {
            return decorateResponse(makeClone());
        };
        return response;
    }
    function _applyBeforeRequestHooks() {
        _applyBeforeRequestHooks = /**
     * Мутирует self.request.
     * Если middleware вернул Response, возвращает его.
     * Иначе возвращает undefined.
     * */ _async_to_generator(function() {
            var _iterator, _step, middleware, result;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _iterator = _create_for_of_iterator_helper_loose(self.options.middlewares.beforeRequest);
                        _state.label = 1;
                    case 1:
                        if (!!(_step = _iterator()).done) return [
                            3,
                            4
                        ];
                        middleware = _step.value;
                        return [
                            4,
                            middleware(self.request, self.options)
                        ];
                    case 2:
                        result = _state.sent();
                        if (isRequest(result)) {
                            self.request = result;
                            return [
                                3,
                                4
                            ];
                        }
                        if (isResponse(result)) {
                            return [
                                2,
                                result
                            ];
                        }
                        _state.label = 3;
                    case 3:
                        return [
                            3,
                            1
                        ];
                    case 4:
                        return [
                            2
                        ];
                }
            });
        });
        return _applyBeforeRequestHooks.apply(this, arguments);
    }
    function _applyAfterResponseHooks() {
        _applyAfterResponseHooks = /** Не мутирует Response. */ _async_to_generator(function(response) {
            var modifiedResponse, _iterator, _step, middleware, responseCandidate, nextResponse;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        /* Не клонируем, если нет middleware */ modifiedResponse = response;
                        _iterator = _create_for_of_iterator_helper_loose(self.options.middlewares.afterResponse);
                        _state.label = 1;
                    case 1:
                        if (!!(_step = _iterator()).done) return [
                            3,
                            4
                        ];
                        middleware = _step.value;
                        responseCandidate = decorateResponse(modifiedResponse.clone());
                        return [
                            4,
                            middleware(self.request, self.options, responseCandidate)
                        ];
                    case 2:
                        nextResponse = _state.sent();
                        if (isResponse(nextResponse)) {
                            modifiedResponse = nextResponse;
                        }
                        _state.label = 3;
                    case 3:
                        return [
                            3,
                            1
                        ];
                    case 4:
                        return [
                            2,
                            modifiedResponse
                        ];
                }
            });
        });
        return _applyAfterResponseHooks.apply(this, arguments);
    }
    function _applyBeforeErrorHooks() {
        _applyBeforeErrorHooks = /** Мутирует Error */ _async_to_generator(function(httpError) {
            var error, _iterator, _step, middleware;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        error = httpError;
                        _iterator = _create_for_of_iterator_helper_loose(self.options.middlewares.beforeError);
                        _state.label = 1;
                    case 1:
                        if (!!(_step = _iterator()).done) return [
                            3,
                            4
                        ];
                        middleware = _step.value;
                        return [
                            4,
                            middleware(error)
                        ];
                    case 2:
                        error = _state.sent();
                        _state.label = 3;
                    case 3:
                        return [
                            3,
                            1
                        ];
                    case 4:
                        return [
                            2,
                            error
                        ];
                }
            });
        });
        return _applyBeforeErrorHooks.apply(this, arguments);
    }
    function _execute() {
        _execute = /**
     * Выполняет запрос, применяя все middleware.
     * */ _async_to_generator(function() {
            var unsafeTimeoutRange, originalResponse, response, requestFailed;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        unsafeTimeoutRange = !!self.options.timeout && self.options.timeout > maxSafeTimeout;
                        invariantSync(unsafeTimeoutRange, function() {
                            return new TimeoutRangeError();
                        });
                        /*
         * Откладываем выполнение запроса, чтобы `body`-методы Response
         * могли устанавливать заголовок `Accept`.
         *
         * @example
         * // Заметьте, мы не ожидаем разрешения promise после запроса,
         * // поэтому нам требуется отложить запрос, чтобы вызовом body-метода
         * // установить заголовок.
         * client.get('/some').json() // Accept: application/json
         * client.get('/some').blob() // Accept: */ /*
         * */ return [
                            4,
                            Promise.resolve()
                        ];
                    case 1:
                        _state.sent();
                        return [
                            4,
                            fetch()
                        ];
                    case 2:
                        originalResponse = _state.sent();
                        return [
                            4,
                            applyAfterResponseHooks(originalResponse)
                        ];
                    case 3:
                        response = decorateResponse.apply(void 0, [
                            _state.sent()
                        ]);
                        requestFailed = !response.ok && self.options.throwHttpErrors;
                        return [
                            4,
                            invariant(requestFailed, function() {
                                return applyBeforeErrorHooks(new HTTPError(response, self.request, self.options));
                            })
                        ];
                    case 4:
                        _state.sent();
                        /*
         * Если передано `onDownloadProgress`, требуется использовать Streams API
         * для отслеживания прогресса получения данных.
         * */ if (self.options.onDownloadProgress) {
                            invariantSync(!supportsResponseStreams, function() {
                                return new ResponseStreamsSupportError();
                            });
                            return [
                                2,
                                watchDownloadStream(response.clone(), self.options.onDownloadProgress)
                            ];
                        }
                        return [
                            2,
                            response
                        ];
                }
            });
        });
        return _execute.apply(this, arguments);
    }
    return self;
};

export { Client };
